import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { DateTimeField } from "@/components/site/DateTimeField";
import { useApi, errMsg } from "@/lib/api";

const EMPTY = { code: "", kind: "flat", value: 200, max_discount: 0, min_order: 0, expires_on: "", usage_limit: 0, per_user_limit: 1, first_trip_only: false, active: true };
const NUMS = ["value", "max_discount", "min_order", "usage_limit", "per_user_limit"];
const inputCls = "mt-1 w-full rounded-md border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#FF5A00]";

const Num = ({ k, label, hint, f, set }) => (
  <label className="text-xs text-neutral-400">{label}
    <input type="number" min="0" value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`coupon-form-${k}`} className={inputCls} />
    {hint && <span className="mt-1 block text-[11px] text-neutral-500">{hint}</span>}
  </label>
);

export function CouponFormDialog({ coupon, onClose, onSaved }) {
  const api = useApi();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (coupon !== undefined) setF(coupon ? { ...EMPTY, ...coupon } : EMPTY);
  }, [coupon]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    const { used_count, owner_user_id, created_at, source, ...body } = f;
    NUMS.forEach((k) => (body[k] = Number(body[k] || 0)));
    body.code = body.code.trim().toUpperCase();
    setBusy(true);
    try {
      if (coupon) await api.put(`/admin/coupons/${coupon.code}`, body);
      else await api.post("/admin/coupons", body);
      toast.success(coupon ? "Coupon updated" : "Coupon created");
      onSaved();
      onClose();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <Dialog open={coupon !== undefined} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="coupon-form-dialog">
        <DialogHeader><DialogTitle>{coupon ? `Edit ${coupon.code}` : "New coupon"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-neutral-400">Code
              <input required disabled={!!coupon} value={f.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="e.g. FIRST500" data-testid="coupon-form-code" className={`${inputCls} font-mono uppercase disabled:opacity-60`} />
            </label>
            <label className="text-xs text-neutral-400">Type
              <select value={f.kind} onChange={(e) => set("kind", e.target.value)} data-testid="coupon-form-kind" className={inputCls}>
                <option value="flat">Flat ₹ off</option><option value="percent">% off</option>
              </select>
            </label>
            <Num k="value" label={f.kind === "flat" ? "Discount (₹)" : "Discount (%)"} f={f} set={set} />
            {f.kind === "percent" && <Num k="max_discount" label="Max discount (₹)" hint="0 = no cap" f={f} set={set} />}
            <Num k="min_order" label="Minimum booking value (₹, before GST)" hint="0 = no minimum" f={f} set={set} />
            <Num k="usage_limit" label="Total uses allowed" hint="0 = unlimited" f={f} set={set} />
            <Num k="per_user_limit" label="Uses per customer" hint="0 = unlimited" f={f} set={set} />
            <div className="text-xs text-neutral-400">Expires on (last valid day)
              <DateTimeField value={f.expires_on} onChange={(v) => set("expires_on", v)} withTime={false} testId="coupon-form-expires" className={inputCls} />
              {f.expires_on && <button type="button" onClick={() => set("expires_on", "")} className="mt-1 text-[11px] text-neutral-500 hover:text-rose-400">Remove expiry</button>}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.first_trip_only} onCheckedChange={(v) => set("first_trip_only", v)} data-testid="coupon-form-first-trip" />First trip only</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => set("active", v)} data-testid="coupon-form-active" />Active</label>
          <button disabled={busy} data-testid="coupon-form-save" className="w-full rounded-md bg-[#FF5A00] py-2.5 text-sm font-semibold text-white disabled:opacity-50">{busy ? "Saving…" : "Save coupon"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
